/**
 * ============================================================================
 * ProjectTable
 * ----------------------------------------------------------------------------
 * Componente de tabela padrão do projeto JF.
 *
 * Funcionalidades:
 *  • filtros persistentes
 *  • ordenação
 *  • drag & drop de colunas
 *  • colunas sticky
 *  • larguras persistentes
 *  • mostrar/ocultar colunas
 *  • ações
 *  • loading
 *  • toolbar
 *
 * Todas as novas tabelas do projeto deverão utilizar este componente.
 * ============================================================================
 */

// src\pages\components\ProjectTable\ProjectTable.js

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTable, useFilters, useSortBy, useColumnOrder } from "react-table";

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";

import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import PushPinIcon from "@mui/icons-material/PushPin";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import "./ProjectTable.css";

const ACTION_BUTTON_SIZE = 32;
const ACTION_BUTTON_GAP = 4;
const ACTIONS_COLUMN_PADDING = 12;

function normalizarTextoPesquisa(valor) {
  return String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[.,]/g, " ")
    .trim();
}

function filtroTextoAnd(rows, ids, filterValue) {
  const termos = normalizarTextoPesquisa(filterValue).split(/\s+/).filter(Boolean);

  if (termos.length === 0) return rows;

  return rows.filter((row) => {
    const texto = normalizarTextoPesquisa(row.values[ids[0]]);

    return termos.every((termo) => texto.includes(termo));
  });
}

filtroTextoAnd.autoRemove = (val) => !val;

function DefaultColumnFilter({ column }) {
  const { filterValue, setFilter } = column;

  return (
    <input
      className="project-table-filter"
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="Filtrar..."
    />
  );
}

DefaultColumnFilter.propTypes = {
  column: PropTypes.object.isRequired,
};

function SimNaoColumnFilter({ column }) {
  const { filterValue, setFilter } = column;

  return (
    <select
      className="project-table-filter"
      value={filterValue ?? ""}
      onChange={(e) => setFilter(e.target.value === "" ? undefined : Number(e.target.value))}
    >
      <option value="">Todos</option>
      <option value="1">Sim</option>
      <option value="0">Não</option>
    </select>
  );
}

SimNaoColumnFilter.propTypes = {
  column: PropTypes.object.isRequired,
};

function SelectOptionsColumnFilter({ column }) {
  const { filterValue, setFilter, filterOptions } = column;

  const options = Array.isArray(filterOptions) ? filterOptions : [];

  return (
    <select
      className="project-table-filter"
      value={filterValue ?? ""}
      onChange={(e) => setFilter(e.target.value === "" ? undefined : e.target.value)}
    >
      <option value="">Todos</option>

      {options.map((option) => (
        <option key={String(option.value)} value={String(option.value)}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

SelectOptionsColumnFilter.propTypes = {
  column: PropTypes.object.isRequired,
};

function filtroSelectExact(rows, ids, filterValue) {
  return rows.filter((row) => String(row.values[ids[0]] ?? "") === String(filterValue ?? ""));
}

filtroSelectExact.autoRemove = (val) => val === undefined || val === null || val === "";

function filtroSimNao(rows, ids, filterValue) {
  return rows.filter((row) => Number(row.values[ids[0]] || 0) === Number(filterValue));
}

filtroSimNao.autoRemove = (val) => val === undefined || val === null || val === "";

function filtroSimNaoInverso(rows, ids, filterValue) {
  return rows.filter(
    (row) => Number(row.values[ids[0]] || 0) === (Number(filterValue) === 1 ? 0 : 1)
  );
}

filtroSimNaoInverso.autoRemove = (val) => val === undefined || val === null || val === "";

function SortableHeader({ id, children, disabled, pinned, onTogglePin }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} className={disabled ? "" : "project-table-sortable-header"} style={style}>
      {!disabled && (
        <span
          className={pinned ? "project-table-pin active" : "project-table-pin"}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onTogglePin?.();
          }}
          title={pinned ? "Remover fixação até esta coluna" : "Fixar esta coluna e anteriores"}
        >
          <PushPinIcon fontSize="inherit" />
        </span>
      )}

      {!disabled && (
        <span className="project-table-drag-handle" {...attributes} {...listeners}>
          <DragIndicatorIcon fontSize="inherit" />
        </span>
      )}

      {children}
    </div>
  );
}

SortableHeader.defaultProps = {
  disabled: false,
  pinned: false,
  onTogglePin: null,
};

SortableHeader.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  pinned: PropTypes.bool,
  onTogglePin: PropTypes.func,
};

function RowActionsCell({ row, rowActions }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItems, setMenuItems] = useState([]);

  const original = row.original;

  const resolve = (value, fallback = undefined) =>
    typeof value === "function" ? value(original) : value ?? fallback;

  return (
    <MDBox display="flex" gap={0.5} alignItems="center">
      {(rowActions || [])
        .filter((a) =>
          typeof a.visible === "function" ? a.visible(original) : a.visible !== false
        )
        .map((a) => {
          const icon = resolve(a.icon);
          const tooltip = resolve(a.tooltip, "");
          const color = resolve(a.color, "primary");
          const disabled = Boolean(resolve(a.disabled, false));

          return (
            <Tooltip title={tooltip || ""} key={a.id}>
              <IconButton
                size="small"
                color={color || "default"}
                disabled={disabled}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.08)",
                  },
                }}
                onClick={(e) => {
                  if (Array.isArray(a.menu) && a.menu.length > 0) {
                    setMenuItems(a.menu);
                    setMenuAnchor(e.currentTarget);
                    return;
                  }

                  a.onClick?.(original);
                }}
              >
                {icon}
              </IconButton>
            </Tooltip>
          );
        })}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {menuItems.map((m, idx) => (
          <MenuItem
            key={m.id || m.label || idx}
            onClick={() => {
              setMenuAnchor(null);
              m.onClick?.(original);
            }}
          >
            {m.label}
          </MenuItem>
        ))}
      </Menu>
    </MDBox>
  );
}

RowActionsCell.propTypes = {
  row: PropTypes.object.isRequired,
  rowActions: PropTypes.array.isRequired,
};

function safeReadState(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function safeWriteState(storageKey, value) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // ignore
  }
}

function safeRemoveState(storageKey) {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
}

export default function ProjectTable({
  tableKey,
  version,
  title,
  titleNote,
  subtitle,
  columns,
  data,
  loading,
  emptyMessage,
  minWidth,
  maxHeight,
  rowActions,
  actionsPosition,
  toolbarActions,
  toolbarLeft,
  defaultSortBy,
  filterTypes,
  onVisibleRowsChange,
  renderAfterRow,
  showKeepFilters,
}) {
  const storageKey = `projectTable:${tableKey}:${version}`;
  const saved = useMemo(() => safeReadState(storageKey) || {}, [storageKey]);

  const [keepFilters, setKeepFilters] = useState(saved.keepFilters !== false);
  const [widths, setWidths] = useState(saved.widths || {});
  const [hiddenColumns, setHiddenColumns] = useState(saved.hiddenColumns || []);
  const [columnOrder, setColumnOrder] = useState(saved.columnOrder || []);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [stickyLeftCount, setStickyLeftCount] = useState(saved.stickyLeftCount || 0);
  const [dragOverInvalidId, setDragOverInvalidId] = useState(null);

  const resizingRef = useRef(null);
  const headerCellRefs = useRef({});
  const [stickyLeftOffsets, setStickyLeftOffsets] = useState({});

  const columnsFinal = useMemo(() => {
    const base = Array.isArray(columns) ? columns : [];

    const cols = base.map((col) => {
      const id = col.id || col.accessor;
      const savedWidth = id ? widths[id] : null;
      const width = savedWidth || col.width || col.defaultWidth;

      const isSimNao = col.filterType === "simNao" || col.filterType === "simNaoInverso";
      const isSelectOptions = col.filterType === "selectExact";

      return {
        ...col,
        width,
        minWidth: col.minWidth || 60,
        maxWidth: col.maxWidth || 900,
        Filter:
          col.Filter ||
          (isSimNao
            ? SimNaoColumnFilter
            : isSelectOptions
            ? SelectOptionsColumnFilter
            : DefaultColumnFilter),
        filter: col.filter || col.filterType || "textoAnd",
        disableFilters: col.filterable === false || col.disableFilters,
        disableSortBy: col.sortable === false || col.disableSortBy,
      };
    });

    if (Array.isArray(rowActions) && rowActions.length > 0) {
      const actionsWidth =
        ACTIONS_COLUMN_PADDING * 2 +
        rowActions.length * ACTION_BUTTON_SIZE +
        Math.max(0, rowActions.length - 1) * ACTION_BUTTON_GAP;

      const actionsColumn = {
        Header: "",
        id: "__actions",
        width: actionsWidth,
        minWidth: actionsWidth,
        maxWidth: actionsWidth,
        disableFilters: true,
        disableSortBy: true,
        resizable: false,
        Cell: ({ row }) => <RowActionsCell row={row} rowActions={rowActions} />,
      };

      if (actionsPosition === "right") {
        cols.push(actionsColumn);
      } else {
        cols.unshift(actionsColumn);
      }
    }

    return cols;
  }, [columns, rowActions, widths, actionsPosition]);

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
      filter: "textoAnd",
    }),
    []
  );

  const sortTypes = useMemo(
    () => ({
      numPt: (rowA, rowB, columnId) => {
        const parse = (v) => {
          if (v == null) return Number.NEGATIVE_INFINITY;
          if (typeof v === "number") return v;
          const s = String(v).trim().replace(/\s+/g, " ").split(" ")[0];
          const n = Number(s.replace(",", "."));
          return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
        };

        const a = parse(rowA.values[columnId]);
        const b = parse(rowB.values[columnId]);

        if (a === b) return 0;
        return a > b ? 1 : -1;
      },
      textPt: (rowA, rowB, columnId) => {
        const a = String(rowA.values[columnId] ?? "").toLowerCase();
        const b = String(rowB.values[columnId] ?? "").toLowerCase();
        return a.localeCompare(b, "pt-PT");
      },
    }),
    []
  );

  const savedFilters = Array.isArray(saved.filters) ? saved.filters : [];
  const savedSortBy = Array.isArray(saved.sortBy) ? saved.sortBy : defaultSortBy || [];

  const tableInstance = useTable(
    {
      columns: columnsFinal,
      data: Array.isArray(data) ? data : [],
      defaultColumn,
      sortTypes,
      filterTypes: {
        textoAnd: filtroTextoAnd,
        simNao: filtroSimNao,
        simNaoInverso: filtroSimNaoInverso,
        selectExact: filtroSelectExact,
        ...(filterTypes || {}),
      },
      initialState: {
        filters: keepFilters ? savedFilters : [],
        sortBy: savedSortBy,
        hiddenColumns,
        columnOrder,
      },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy,
    useColumnOrder
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setAllFilters,
    setSortBy,
    setHiddenColumns: setTableHiddenColumns,
    allColumns,
    visibleColumns,
    setColumnOrder: setTableColumnOrder,
  } = tableInstance;

  const activeFiltersCount = state.filters?.length || 0;

  const visibleOriginalRows = useMemo(() => rows.map((row) => row.original), [rows]);

  useEffect(() => {
    onVisibleRowsChange?.(visibleOriginalRows);
  }, [onVisibleRowsChange, visibleOriginalRows]);

  const stickyLeftColumnIds = [
    ...(visibleColumns.some((c) => c.id === "__actions") ? ["__actions"] : []),
    ...visibleColumns
      .filter((c) => c.id !== "__actions")
      .slice(0, stickyLeftCount)
      .map((c) => c.id),
  ];

  const getColumnWidth = (column) => {
    const width = Number(column?.width || 120);
    const min = Number(column?.minWidth || 60);
    const max = Number(column?.maxWidth || 900);

    return Math.max(min, Math.min(max, width));
  };

  const getStickyLeftOffset = (columnId) => {
    return stickyLeftOffsets[columnId];
  };

  const configuredMinWidth = Number.parseFloat(minWidth) || 900;

  const tableWidth = Math.max(
    configuredMinWidth,
    visibleColumns.reduce((sum, column) => sum + getColumnWidth(column), 0)
  );

  useLayoutEffect(() => {
    const calcularOffsets = () => {
      let left = 0;
      const nextOffsets = {};

      stickyLeftColumnIds.forEach((columnId) => {
        nextOffsets[columnId] = left;

        const element = headerCellRefs.current[columnId];
        if (element) {
          left += element.offsetWidth;
        }
      });

      setStickyLeftOffsets((prev) => {
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(nextOffsets);

        const igual =
          prevKeys.length === nextKeys.length &&
          nextKeys.every((key) => prev[key] === nextOffsets[key]);

        return igual ? prev : nextOffsets;
      });
    };

    calcularOffsets();

    const observer = new ResizeObserver(calcularOffsets);

    Object.values(headerCellRefs.current).forEach((element) => {
      if (element) observer.observe(element);
    });

    window.addEventListener("resize", calcularOffsets);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", calcularOffsets);
    };
  }, [stickyLeftColumnIds.join("|"), widths, hiddenColumns, columnOrder, visibleColumns.length]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    safeWriteState(storageKey, {
      keepFilters,
      filters: keepFilters ? state.filters || [] : [],
      sortBy: state.sortBy || [],
      widths,
      hiddenColumns,
      columnOrder,
      stickyLeftCount,
    });
  }, [
    storageKey,
    keepFilters,
    state.filters,
    state.sortBy,
    widths,
    hiddenColumns,
    columnOrder,
    stickyLeftCount,
  ]);

  const resetConfig = () => {
    safeRemoveState(storageKey);
    setAllFilters([]);
    setSortBy(defaultSortBy || []);
    setWidths({});
    setKeepFilters(true);
    setHiddenColumns([]);
    setColumnOrder([]);
    setTableColumnOrder([]);
    setStickyLeftCount(0);
  };

  const startResize = (e, column) => {
    if (column.resizable === false) return;

    e.preventDefault();
    e.stopPropagation();

    resizingRef.current = {
      id: column.id,
      startX: e.clientX,
      startWidth: Number(column.width || 120),
      minWidth: Number(column.minWidth || 60),
      maxWidth: Number(column.maxWidth || 900),
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      const r = resizingRef.current;
      if (!r) return;

      const delta = e.clientX - r.startX;
      const next = Math.max(r.minWidth, Math.min(r.maxWidth, r.startWidth + delta));

      setWidths((prev) => ({
        ...prev,
        [r.id]: next,
      }));
    };

    const onUp = () => {
      resizingRef.current = null;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const toggleStickyColumn = (columnId) => {
    if (!columnId || columnId === "__actions") return;

    const visibleNormalColumns = visibleColumns.filter((c) => c.id !== "__actions");
    const index = visibleNormalColumns.findIndex((c) => c.id === columnId);

    if (index < 0) return;

    const nextCount = index + 1;

    setStickyLeftCount((prev) => (prev === nextCount ? 0 : nextCount));
  };

  const isInvalidDragTarget = (activeId, overId) => {
    if (!activeId || !overId) return false;
    if (activeId === overId) return false;
    if (activeId === "__actions" || overId === "__actions") return false;
    if (stickyLeftCount <= 0) return false;

    const currentOrder = visibleColumns.map((c) => c.id).filter((id) => id !== "__actions");

    const activeIndex = currentOrder.indexOf(activeId);
    const overIndex = currentOrder.indexOf(overId);

    if (activeIndex < 0 || overIndex < 0) return false;

    const activeIsSticky = activeIndex < stickyLeftCount;
    const overIsSticky = overIndex < stickyLeftCount;

    return activeIsSticky !== overIsSticky;
  };

  const handleDragEnd = (event) => {
    setDragOverInvalidId(null);

    const { active, over } = event;

    if (!active?.id || !over?.id) return;
    if (active.id === over.id) return;
    if (active.id === "__actions" || over.id === "__actions") return;

    const currentOrder = visibleColumns.map((c) => c.id).filter((id) => id !== "__actions");

    const oldIndex = currentOrder.indexOf(active.id);
    const newIndex = currentOrder.indexOf(over.id);

    if (oldIndex < 0 || newIndex < 0) return;

    // Se existirem colunas fixas, não permitir drag entre zona fixa e zona livre.
    // Para mudar uma coluna de zona, usa-se o pin.
    if (stickyLeftCount > 0) {
      const activeIsSticky = oldIndex < stickyLeftCount;
      const overIsSticky = newIndex < stickyLeftCount;

      if (activeIsSticky !== overIsSticky) return;
    }

    const nextVisibleOrder = arrayMove(currentOrder, oldIndex, newIndex);

    const nextOrder = allColumns.some((c) => c.id === "__actions")
      ? ["__actions", ...nextVisibleOrder]
      : nextVisibleOrder;

    setColumnOrder(nextOrder);
    setTableColumnOrder(nextOrder);
  };

  const toggleColumnHidden = (columnId) => {
    setHiddenColumns((prev) => {
      const exists = prev.includes(columnId);
      const next = exists ? prev.filter((x) => x !== columnId) : [...prev, columnId];

      setTableHiddenColumns(next);

      return next;
    });
  };

  return (
    <Card>
      <MDBox p={2}>
        {(title || titleNote || subtitle || showKeepFilters || toolbarActions?.length > 0) && (
          <MDBox
            mb={1.5}
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
          >
            {(title || titleNote || subtitle) && (
              <MDBox>
                {title && <MDTypography variant="h6">{title}</MDTypography>}

                {titleNote && (
                  <MDTypography
                    sx={{
                      display: "block",
                      mt: 0.25,
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1.35,
                    }}
                  >
                    {titleNote}
                  </MDTypography>
                )}

                {subtitle && (
                  <MDTypography variant="caption" color="text">
                    {subtitle}
                  </MDTypography>
                )}
              </MDBox>
            )}

            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {toolbarLeft}

                {showKeepFilters && (
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={keepFilters}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setKeepFilters(checked);

                          if (!checked) {
                            setAllFilters([]);
                            safeWriteState(storageKey, {
                              ...safeReadState(storageKey),
                              keepFilters: false,
                              filters: [],
                            });
                          }
                        }}
                      />
                    }
                    label={
                      <span
                        style={{
                          fontSize: 12,
                          color: activeFiltersCount > 0 ? "#d97706" : undefined,
                          fontWeight: activeFiltersCount > 0 ? 600 : 400,
                        }}
                      >
                        Manter filtros
                        {activeFiltersCount > 0 ? ` (${activeFiltersCount} filtros)` : ""}
                      </span>
                    }
                  />
                )}

                <Tooltip title="Mostrar/ocultar colunas">
                  <IconButton size="small" onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}>
                    <ViewColumnIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Menu
                  anchorEl={columnsMenuAnchor}
                  open={Boolean(columnsMenuAnchor)}
                  onClose={() => setColumnsMenuAnchor(null)}
                >
                  {allColumns
                    .filter((col) => col.id !== "__actions" && col.hideable !== false)
                    .map((col) => (
                      <MenuItem key={col.id} onClick={() => toggleColumnHidden(col.id)}>
                        <Checkbox size="small" checked={!hiddenColumns.includes(col.id)} />
                        {typeof col.Header === "string" ? col.Header : col.id}
                      </MenuItem>
                    ))}
                </Menu>

                <Tooltip title="Repor configuração da tabela">
                  <IconButton size="small" onClick={resetConfig}>
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MDBox>

              {(toolbarActions || []).length > 0 && (
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={1}
                  ml={3}
                  pl={3}
                  borderLeft="1px solid #e0e0e0"
                  flexWrap="wrap"
                >
                  {(toolbarActions || []).map((a) => (
                    <MDButton
                      key={a.id}
                      color={a.color || "info"}
                      variant={a.variant || "gradient"}
                      size="small"
                      disabled={!!a.disabled}
                      onClick={a.onClick}
                    >
                      {a.label}
                    </MDButton>
                  ))}
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        )}

        {loading && (
          <MDBox py={2} display="flex" justifyContent="center">
            <CircularProgress size={24} />
          </MDBox>
        )}

        <div
          className="project-table-wrap"
          style={{
            maxHeight: maxHeight || undefined,
          }}
        >
          <table
            {...getTableProps()}
            className="project-table"
            style={{
              width: tableWidth,
              minWidth: tableWidth,
              tableLayout: "auto",
            }}
          >
            <thead>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragOver={({ active, over }) => {
                  if (isInvalidDragTarget(active?.id, over?.id)) {
                    setDragOverInvalidId(over.id);
                  } else {
                    setDragOverInvalidId(null);
                  }
                }}
                onDragCancel={() => setDragOverInvalidId(null)}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={visibleColumns.map((c) => c.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                      {hg.headers.map((col) => {
                        const filtered = (state.filters || []).some((f) => f.id === col.id);
                        const width = getColumnWidth(col);

                        return (
                          <th
                            {...col.getHeaderProps(col.getSortByToggleProps())}
                            ref={(element) => {
                              if (element) {
                                headerCellRefs.current[col.id] = element;
                              } else {
                                delete headerCellRefs.current[col.id];
                              }
                            }}
                            key={col.id}
                            className={[
                              filtered ? "filtered" : "",
                              col.id === "__actions" ? "project-table-actions-header" : "",
                              dragOverInvalidId === col.id ? "project-table-invalid-drop" : "",
                              stickyLeftColumnIds.filter((id) => id !== "__actions").at(-1) ===
                              col.id
                                ? "project-table-last-sticky"
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            style={{
                              width,
                              minWidth: col.minWidth || 60,
                              maxWidth: col.maxWidth || 900,
                              boxSizing: "border-box",
                              position: "sticky",
                              left: stickyLeftColumnIds.includes(col.id)
                                ? getStickyLeftOffset(col.id)
                                : undefined,
                              zIndex: stickyLeftColumnIds.includes(col.id) ? 5 : 2,
                            }}
                          >
                            <SortableHeader
                              id={col.id}
                              disabled={col.id === "__actions"}
                              pinned={
                                col.id !== "__actions" &&
                                visibleColumns
                                  .filter((c) => c.id !== "__actions")
                                  .slice(0, stickyLeftCount)
                                  .some((c) => c.id === col.id)
                              }
                              onTogglePin={() => toggleStickyColumn(col.id)}
                            >
                              <MDBox display="flex" alignItems="center" gap={0.5}>
                                {col.render("Header")}
                                {filtered && <FilterAltIcon sx={{ fontSize: 14 }} />}
                                {col.isSorted ? (col.isSortedDesc ? " ↓" : " ↑") : ""}
                              </MDBox>
                            </SortableHeader>

                            {!col.disableFilters && (
                              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                                {col.canFilter ? col.render("Filter") : null}
                              </div>
                            )}

                            {col.resizable !== false && (
                              <span
                                className="project-table-resizer"
                                onMouseDown={(e) => startResize(e, col)}
                              />
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </SortableContext>
              </DndContext>
            </thead>

            <tbody {...getTableBodyProps()}>
              {rows.map((row, rowIndex) => {
                prepareRow(row);

                const afterRow =
                  typeof renderAfterRow === "function"
                    ? renderAfterRow({
                        row,
                        rowIndex,
                        rows,
                        visibleColumns,
                      })
                    : null;

                return (
                  <React.Fragment key={row.id}>
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          key={cell.column.id}
                          style={{
                            textAlign: cell.column.align || "left",
                            width: getColumnWidth(cell.column),
                            minWidth: cell.column.minWidth || 60,
                            maxWidth: cell.column.maxWidth || 900,
                            boxSizing: "border-box",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            position: stickyLeftColumnIds.includes(cell.column.id)
                              ? "sticky"
                              : undefined,
                            left: stickyLeftColumnIds.includes(cell.column.id)
                              ? getStickyLeftOffset(cell.column.id)
                              : undefined,
                            zIndex: stickyLeftColumnIds.includes(cell.column.id) ? 3 : undefined,
                            background: stickyLeftColumnIds.includes(cell.column.id)
                              ? "#fff"
                              : undefined,
                          }}
                          className={
                            stickyLeftColumnIds.filter((id) => id !== "__actions").at(-1) ===
                            cell.column.id
                              ? "project-table-last-sticky"
                              : ""
                          }
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>

                    {afterRow}
                  </React.Fragment>
                );
              })}

              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={visibleColumns.length} className="project-table-empty">
                    {emptyMessage || "Sem dados para apresentar."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </MDBox>
    </Card>
  );
}

ProjectTable.defaultProps = {
  version: "v1",
  title: "",
  titleNote: "",
  subtitle: "",
  data: [],
  loading: false,
  emptyMessage: "Sem dados para apresentar.",
  minWidth: 900,
  maxHeight: null,
  rowActions: [],
  actionsPosition: "left",
  toolbarActions: [],
  toolbarLeft: null,
  defaultSortBy: [],
  filterTypes: {},
  onVisibleRowsChange: null,
  renderAfterRow: null,
  showKeepFilters: true,
};

ProjectTable.propTypes = {
  tableKey: PropTypes.string.isRequired,
  version: PropTypes.string,
  title: PropTypes.string,
  titleNote: PropTypes.node,
  subtitle: PropTypes.string,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rowActions: PropTypes.array,
  actionsPosition: PropTypes.oneOf(["left", "right"]),
  toolbarActions: PropTypes.array,
  toolbarLeft: PropTypes.node,
  defaultSortBy: PropTypes.array,
  filterTypes: PropTypes.object,
  onVisibleRowsChange: PropTypes.func,
  renderAfterRow: PropTypes.func,
  showKeepFilters: PropTypes.bool,
};
